'use client';
import React, { useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { signIn, signOut, useSession as useAuthSession } from 'next-auth/react';
import { interpolate } from '@format/interpolate';
import { Icon } from '@ui/icons';
import { ButtonLink, ResponsiveText } from '@web/atoms';
import { TrackedLink } from '../TrackedLink';
import { AddPupButton } from '../AddPupButton';
import { SlidingContainer } from '../SlidingContainer';
import { ResponsiveImage } from '../ResponsiveImage';
import { CtaButton } from '../CtaButton';
import { SlideoutMenuLink } from '../SlideoutMenuLink';
import { SlideDownMenuLink } from '../SlideDownMenuLink';
import { useSessionCustomer } from '../../customer';
import { shopifyLogoutRedirect } from '../../auth/ShopifyAuth/api';
import { useToast, TOAST_CLIENT_ERROR_MESSAGE } from '../../Toast';
import { useShopifyOfferContext } from '../../offer/shopifyContext';
import { getStartedButtonClicked } from './events';
import { NavLinkGroup } from './NavLinkGroup';
import { ReferAFriendLink, HeaderLink } from './HeaderLink';
import { ProfileIcon } from './ProfileIcon';
import {
  DesktopNavigationMenu,
  MobileNavigationMenu,
  BurgerMenu,
  SlideOutMenu,
  NavigationButton,
  MobileCtaButtonContainer,
  MobileButtonWrapper,
  NavigationMenuLink,
  SlideOutMenuTopSection,
  SlideOutMenuCloseButton,
  LogoImgContainer,
  MobileDrawerScrollArea,
} from './NavigationBar/styles.js';
import {
  SmallNavLink,
  StyledLoginButton,
  StyledProfileWrapper,
  DesktopButtonWrapper,
} from './styles';
import OllieLogo from './NavigationBar/ollie-logo.svg';

const defaultCtas = [
  {
    mobileCTAText: 'Get Started',
    desktopCTAText: 'Get Started',
    ctaTarget: '/onboarding/',
    ctaVariant: 'yellow',
  },
];

export const LoginMenuLinks = ({
  ctas = defaultCtas,
  hideLoginLink = false,
}) => {
  const { formattedAmount } = useShopifyOfferContext();

  return (
    <DesktopButtonWrapper>
      {ctas?.map((cta, index) => {
        const desktopCTATextWithDiscount = interpolate(
          cta.desktopCTAText ?? 'Get Started',
          {
            discount: formattedAmount ?? '50%',
          },
        );

        const mobileCTATextWithDiscount = interpolate(
          cta.mobileCTAText ?? 'Get Started',
          {
            discount: formattedAmount ?? '50%',
          },
        );

        return (
          <NavigationButton key={index}>
            <TrackedLink
              data-testid={index === 0 ? 'nav-get-started' : 'nav-login'}
              variant={cta.ctaVariant ?? 'yellow'}
              verticalPadding={4}
              fullWidth={true}
              analyticsEvent={
                index === 0 ? getStartedButtonClicked() : undefined
              }
              href={cta.ctaTarget ?? '/onboarding/'}
            >
              <ResponsiveText
                desktopText={desktopCTATextWithDiscount}
                mobileText={mobileCTATextWithDiscount}
              />
            </TrackedLink>
          </NavigationButton>
        );
      })}

      {!hideLoginLink && (
        <StyledLoginButton
          showButton={false}
          data-testid="nav-login"
          onClick={() => signIn('shopify', { callbackUrl: '/account/' })}
        >
          Login
        </StyledLoginButton>
      )}
    </DesktopButtonWrapper>
  );
};

LoginMenuLinks.displayName = 'LoginMenuLinks';

LoginMenuLinks.propTypes = {
  ctas: PropTypes.arrayOf(
    PropTypes.shape({
      mobileCTAText: PropTypes.string,
      desktopCTAText: PropTypes.string,
      ctaTarget: PropTypes.string,
      ctaVariant: PropTypes.string,
    }),
  ),
  hideLoginLink: PropTypes.bool,
};

export const LoggedInMenuLink = () => {
  const { customer } = useSessionCustomer();

  return (
    <StyledProfileWrapper>
      <ProfileIcon color="white" />
      <SmallNavLink showLink={true} href="/account/">
        Hi, {customer?.first_name ?? 'friend'}
      </SmallNavLink>
    </StyledProfileWrapper>
  );
};

export const UserMenuLinks = ({ ctas, hideLoginLink = false }) => {
  const { isActive } = useSessionCustomer();
  return isActive ? (
    <LoggedInMenuLink />
  ) : (
    <LoginMenuLinks ctas={ctas} hideLoginLink={hideLoginLink} />
  );
};

export const LeftMenuLinks = ({
  links,
  menuBgColor,
  ctas,
  hideLoginLink,
  logo,
  closeButtonPosition = 'left',
}) => {
  const [showSideMenu, setShowSideMenu] = useState(false);
  const [slidingContainerOffset, setSlidingContainerOffset] = useState(0);
  const { isActive } = useSessionCustomer();
  const { formattedAmount } = useShopifyOfferContext();
  const toggleSideMenu = () => {
    setShowSideMenu(!showSideMenu);
  };

  useLayoutEffect(() => {
    const nav = document.querySelectorAll('[role="navigation"]')[0];
    // In ABTasty, sometimes the nav isn't there as the editor is loading
    if (nav) {
      const { top, height } = nav.getBoundingClientRect();
      setSlidingContainerOffset(top + height);
    }
  }, []);

  const { showToast } = useToast();

  const { data: shopifySession } = useAuthSession();
  const idToken = shopifySession?.user?.id_token;

  const onLogoutClick = async () => {
    const { Gladly } = window;
    if (Gladly && Gladly.clearUser) Gladly.clearUser();
    try {
      toggleSideMenu();
      await signOut({ redirect: false });
      window.location.href = shopifyLogoutRedirect(idToken);
    } catch {
      showToast({
        status: 'error',
        headline: 'Error',
        message: TOAST_CLIENT_ERROR_MESSAGE,
      });
    }
  };

  return (
    <>
      <MobileNavigationMenu>
        <BurgerMenu onClick={toggleSideMenu}>
          {showSideMenu ? (
            <Icon
              color="text.lightest"
              iconName="close"
              alt="burger menu"
              size={1}
              id="close_menu"
            />
          ) : (
            <Icon
              color="text.lightest"
              iconName="menu"
              alt="burger menu"
              size={1}
              id="show_menu"
            />
          )}
        </BurgerMenu>
        <SlidingContainer
          active={showSideMenu}
          onExit={toggleSideMenu}
          bgColor={menuBgColor}
          alignment="left"
          offsetTop={slidingContainerOffset}
          maxWidth={765}
          contentAlignment={
            closeButtonPosition === 'right' ? 'flex-start' : 'center'
          }
        >
          <>
            <SlideOutMenuTopSection
              $hideLoginLink={hideLoginLink}
              closeButtonPosition={closeButtonPosition}
            >
              <SlideOutMenuCloseButton onClick={toggleSideMenu}>
                <Icon
                  color="text.lightest"
                  iconName="close"
                  alt="burger menu"
                  size={1}
                  id="close_menu_slide"
                />
              </SlideOutMenuCloseButton>

              <LogoImgContainer>
                <ResponsiveImage
                  src={logo?.src || OllieLogo.src}
                  alt="Ollie logo"
                  width={logo?.width || OllieLogo.width}
                  height={logo?.height || OllieLogo.height}
                />
              </LogoImgContainer>

              {!isActive && !hideLoginLink && (
                <div>
                  <StyledLoginButton
                    showButton={true}
                    data-testid="nav-login"
                    onClick={() =>
                      signIn('shopify', { callbackUrl: '/account/' })
                    }
                  >
                    Login
                  </StyledLoginButton>
                </div>
              )}
            </SlideOutMenuTopSection>
            <MobileDrawerScrollArea $fillColor={menuBgColor}>
              <SlideOutMenu>
                {links?.map((link, index) => {
                  const { component } = link;
                  if (component === 'nav_link_group') {
                    return (
                      <NavLinkGroup
                        key={link.component + index}
                        index={Number(index)}
                        heading={link.heading}
                        links={link.links}
                        headingFontColor={link.headingFontColor}
                        linkFontColor={link.linkFontColor}
                        closeMenu={toggleSideMenu}
                        menuBgColor={menuBgColor}
                      />
                    );
                  }
                  if (component === 'slideout_menu_link')
                    return (
                      <NavigationMenuLink key={link.component + index}>
                        <SlideoutMenuLink
                          {...link}
                          key={index}
                          onCloseMenu={toggleSideMenu}
                        />
                      </NavigationMenuLink>
                    );
                  if (component === 'slide_down_menu_link')
                    return (
                      <NavigationMenuLink key={link.component + index}>
                        <SlideDownMenuLink
                          bgColor={menuBgColor}
                          {...link}
                          key={index}
                          onCloseMenu={toggleSideMenu}
                        />
                      </NavigationMenuLink>
                    );
                  if (component === 'refer_a_friend_button')
                    return (
                      <NavigationMenuLink key={link.component + index}>
                        <ReferAFriendLink
                          key={index}
                          position={index}
                          {...link}
                          type={'mobile'}
                        />
                      </NavigationMenuLink>
                    );
                  return (
                    <NavigationMenuLink key={link.component + index}>
                      <HeaderLink
                        target={link.target}
                        position={index}
                        text={link.text}
                        type="desktop"
                        key={index}
                        onClick={toggleSideMenu}
                      />
                    </NavigationMenuLink>
                  );
                })}
                {isActive && (
                  <>
                    <MobileButtonWrapper verticalMargin={16}>
                      <AddPupButton
                        href="/account/add-pup/"
                        fullWidth={true}
                        variant="lilac"
                        verticalPadding={3}
                        horizontalPadding={3}
                        minWidth={344}
                        text="Add Pup"
                      />
                    </MobileButtonWrapper>
                    <MobileButtonWrapper verticalMargin={2}>
                      <ButtonLink
                        onClick={onLogoutClick}
                        variant="neutral.white"
                      >
                        Logout
                      </ButtonLink>
                    </MobileButtonWrapper>
                  </>
                )}
              </SlideOutMenu>
              {!isActive && ctas && (
                <MobileCtaButtonContainer
                  $buttonCount={ctas?.length || 1}
                  $bgColor={menuBgColor}
                >
                  {ctas.map((button, key) => (
                    <CtaButton
                      key={key}
                      href={button.ctaTarget}
                      variant={button.ctaVariant}
                      fullWidth={true}
                      onClick={toggleSideMenu}
                      data-testid="nav-get-started-menu"
                    >
                      {button.mobileCTAText
                        ? interpolate(button.mobileCTAText, {
                            discount: formattedAmount ?? '50%',
                          })
                        : 'Get Started'}
                    </CtaButton>
                  ))}
                </MobileCtaButtonContainer>
              )}
            </MobileDrawerScrollArea>
          </>
        </SlidingContainer>
      </MobileNavigationMenu>
    </>
  );
};

LeftMenuLinks.displayName = 'MarketingMenuLinks';

LeftMenuLinks.propTypes = {
  links: PropTypes.arrayOf(
    PropTypes.shape({
      target: PropTypes.string,
      text: PropTypes.string,
      component: PropTypes.string,
    }),
  ),
  menuBgColor: PropTypes.string,
  hideLoginLink: PropTypes.bool,
  logo: PropTypes.object,
  closeButtonPosition: PropTypes.oneOf(['left', 'right']),
};

export const CenterMenuLinks = ({ links }) => {
  return (
    <>
      <DesktopNavigationMenu>
        {links?.map((link, index) => {
          const { component } = link;
          if (component === 'slideout_menu_link')
            return (
              <NavigationMenuLink key={index}>
                <SlideoutMenuLink {...link} />
              </NavigationMenuLink>
            );
          if (component === 'slide_down_menu_link')
            return (
              <NavigationMenuLink key={index}>
                <SlideDownMenuLink {...link} />
              </NavigationMenuLink>
            );
          if (component === 'refer_a_friend_button')
            return (
              <NavigationMenuLink key={index}>
                <ReferAFriendLink position={index} {...link} type={'desktop'} />
              </NavigationMenuLink>
            );
          return (
            <NavigationMenuLink key={index}>
              <HeaderLink {...link} position={index} type="desktop" />
            </NavigationMenuLink>
          );
        })}
      </DesktopNavigationMenu>
    </>
  );
};

CenterMenuLinks.displayName = 'CenterMenuLinks';

CenterMenuLinks.propTypes = {
  links: PropTypes.arrayOf(
    PropTypes.shape({
      target: PropTypes.string,
      text: PropTypes.string,
      component: PropTypes.string,
    }),
  ),
};
